import { types, getParent, getRoot, getSnapshot } from 'mobx-state-tree';
import { v4 as uuidv4 } from 'uuid';

export const Node = types
  .model('Node', {
    id: types.optional(types.identifier, () => uuidv4()),
    type: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    opacity: types.optional(types.number, 1),
    custom: types.optional(types.frozen(), {}),
    visible: types.optional(types.boolean, true),
    selectable: types.optional(types.boolean, true),
    removable: types.optional(types.boolean, true),
    alwaysOnTop: types.optional(types.boolean, false),
    showInExport: types.optional(types.boolean, true),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return snapshot;
    const s = { ...snapshot };

    // Polotno uses 'locked' boolean → map to our interaction flags
    if (s.locked === true) {
      if (s.draggable === undefined) s.draggable = false;
      if (s.resizable === undefined) s.resizable = false;
      if (s.contentEditable === undefined) s.contentEditable = false;
      if (s.styleEditable === undefined) s.styleEditable = false;
    }
    delete s.locked;

    // Coerce opacity from string to number if needed
    if (typeof s.opacity === 'string') {
      s.opacity = parseFloat(s.opacity) || 1;
    }

    return s;
  })
  .views((self) => ({
    get locked() {
      return (
        !self.draggable &&
        !self.resizable &&
        !self.contentEditable &&
        !self.styleEditable
      );
    },
    get page() {
      try {
        let parent = getParent(self);
        // Walk up: element -> children array -> page
        if (parent && getParent(parent)) {
          return getParent(parent);
        }
      } catch (e) {
        // not attached yet
      }
      return undefined;
    },
    get store() {
      try {
        return getRoot(self);
      } catch (e) {
        return undefined;
      }
    },
    get top() {
      // For grouped elements, returns the top-level group. For ungrouped, returns self.
      try {
        let parent = getParent(self);
        if (parent) {
          let grandParent = getParent(parent);
          if (grandParent && grandParent.type === 'group') {
            return grandParent.top;
          }
        }
      } catch (e) {
        // not in a group
      }
      return self;
    },
    get parent() {
      try {
        const arr = getParent(self);
        if (arr) {
          return getParent(arr);
        }
      } catch (e) {
        // not attached
      }
      return undefined;
    },
    get zIndex() {
      try {
        const arr = getParent(self);
        if (arr) {
          return arr.indexOf(self);
        }
      } catch (e) {
        // not attached
      }
      return 0;
    },
  }))
  .actions((self) => ({
    toJSON() {
      const json = {};
      const snapshot = { ...self };
      for (const key of Object.keys(snapshot)) {
        if (key.startsWith('_')) continue;
        json[key] = snapshot[key];
      }
      return json;
    },
  }))
  .actions((self) => ({
    clone(attrs = {}, { skipSelect = false } = {}) {
      const json = { ...getSnapshot(self) };
      delete json.id;
      Object.assign(json, attrs);
      const page = self.page;
      if (page) {
        return page.addElement(json, { skipSelect });
      }
      return null;
    },
    set(attrs) {
      for (const [key, value] of Object.entries(attrs)) {
        if (key in self && key !== 'id') {
          self[key] = value;
        }
      }
    },
    moveUp() {
      const page = self.page;
      if (page) {
        page.moveElementUp(self.id);
      }
    },
    moveTop() {
      const page = self.page;
      if (page) {
        page.moveElementTop(self.id);
      }
    },
    moveDown() {
      const page = self.page;
      if (page) {
        page.moveElementDown(self.id);
      }
    },
    moveBottom() {
      const page = self.page;
      if (page) {
        page.moveElementBottom(self.id);
      }
    },
    setZIndex(zIndex) {
      const page = self.page;
      if (page) {
        page.setElementZIndex(self.id, zIndex);
      }
    },
    beforeDestroy() {
      // cleanup hook
    },
  }));
